import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/App";
import { copy, CONTACT } from "@/i18n";
import { scrollToId } from "@/utils";
import { LogoMark } from "@/components/Logo";

const LINKS = ["about", "menu", "gallery", "reviews", "contact"];

const Navbar = () => {
    const { lang, setLang } = useLang();
    const c = copy[lang];
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="main-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 border-b ${
                scrolled ? "bg-noir/80 backdrop-blur-xl border-white/10" : "bg-noir/40 backdrop-blur-md border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between gap-4">
                <button
                    data-testid="nav-logo"
                    onClick={() => window.__lenis?.scrollTo(0)}
                    className="flex items-center gap-3"
                >
                    <LogoMark className="h-8 sm:h-9" />
                </button>

                <nav className="hidden lg:flex items-center gap-7">
                    {LINKS.map((k) => (
                        <button
                            key={k}
                            data-testid={`nav-link-${k}`}
                            onClick={() => go(k)}
                            className="text-xs font-semibold tracking-[0.18em] uppercase text-neutral-400 hover:text-crimson-bright transition-colors"
                        >
                            {c.nav[k]}
                        </button>
                    ))}
                    <Link
                        data-testid="nav-shop-link"
                        to="/shop"
                        className="text-xs font-semibold tracking-[0.18em] uppercase text-brass hover:text-white transition-colors"
                    >
                        {c.nav.shop}
                    </Link>
                </nav>

                <div className="flex items-center gap-3">
                    <div
                        data-testid="lang-toggle"
                        className="flex items-center rounded-full border border-white/15 overflow-hidden text-[11px] font-mono"
                    >
                        {["it", "en", "de", "fr"].map((l) => (
                            <button
                                key={l}
                                data-testid={`lang-${l}`}
                                onClick={() => setLang(l)}
                                className={`px-3 py-1.5 uppercase transition-colors ${
                                    lang === l ? "bg-crimson text-white" : "text-neutral-400 hover:text-white"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    <Link
                        data-testid="nav-events-cta"
                        to="/eventi"
                        className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#7C3AED]/60 text-[#C4B5FD] hover:bg-[#7C3AED] hover:text-white transition-colors text-xs font-bold tracking-widest uppercase px-5 py-2.5"
                    >
                        {c.nav.events}
                    </Link>
                    <a
                        data-testid="nav-book-cta"
                        href={CONTACT.phoneHref}
                        className="hidden sm:inline-flex items-center gap-2 rounded-full bg-crimson hover:bg-crimson-bright transition-colors text-white text-xs font-bold tracking-widest uppercase px-5 py-2.5"
                    >
                        <Phone size={14} />
                        {c.nav.book}
                    </a>
                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden text-white p-2"
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <MenuIcon size={22} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="lg:hidden bg-noir/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-4"
                    >
                        {LINKS.map((k) => (
                            <button
                                key={k}
                                data-testid={`mobile-nav-link-${k}`}
                                onClick={() => go(k)}
                                className="text-left font-display text-xl uppercase tracking-tight text-neutral-200 hover:text-crimson-bright transition-colors"
                            >
                                {c.nav[k]}
                            </button>
                        ))}
                        <Link
                            data-testid="mobile-nav-shop-link"
                            to="/shop"
                            onClick={() => setOpen(false)}
                            className="text-left font-display text-xl uppercase tracking-tight text-brass"
                        >
                            {c.nav.shop}
                        </Link>
                        <Link
                            data-testid="mobile-nav-events-cta"
                            to="/eventi"
                            onClick={() => setOpen(false)}
                            className="text-left font-display text-xl uppercase tracking-tight text-[#C4B5FD]"
                        >
                            {c.nav.events}
                        </Link>
                        <a
                            data-testid="mobile-book-cta"
                            href={CONTACT.phoneHref}
                            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-crimson text-white text-sm font-bold uppercase tracking-widest px-5 py-3"
                        >
                            <Phone size={16} />
                            {c.nav.book}
                        </a>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
